import {
  Company,
  Country,
  Division,
  ExternalCompany,
  PBIReport,
  Region,
  Subdivision,
  User,
  UserCompanyRole,
  UserCompanyRoles,
  UserGroupRole,
  UserType
} from '@esg/esg-global-types';
import { getExternalCompanies } from '../lib/app/external_company';
import { SiteExtended, getSitesJoinParents } from '../lib/metric_capture/site';
import { getSubdivisions } from '../lib/metric_capture/subdivision';
import { getDivisions } from '../lib/metric_capture/division';
import { MetricExtended, getMetrics } from '../lib/metric_capture/metric';
import { CompanyExtended } from '../lib/app/company';
import {
  View,
  ViewExtended,
  getViews,
  CUSTOM_DASHBOARD_VIEW_ID,
  hasCustomDashboard,
  getModuleId
} from '../lib/app/navigation';
import { ActivityOwner } from '../components/shared/input/select/ActivityOwnerSelect';
import { getCountries } from '../lib/metric_capture/country';
import { getRegions } from '../lib/metric_capture/region';

export type ViewAccess = 'none' | 'read' | 'write' | 'admin' | null;

/**
 * Decide whether `current_user` is allowed to manage (edit / delete / restore /
 * bulk-select) `target_user`. Centralises the rule that previously lived
 * duplicated in `UsersTable.tsx`'s row-action `disabled` conditions.
 *
 * Rules (preserving prior `UsersTable.tsx` behaviour):
 * - No current user → cannot manage.
 * - Acting on self (same email) → always allowed.
 * - Super admin → can manage anyone (incl. other super admins, group admins).
 * - Non super admin → can manage only "normal" users; cannot touch super
 *   admins or any user who is a group admin of any group.
 *
 * Matches on `email` (not `id`) to stay robust to refetched row instances
 * carrying a different `id` reference.
 *
 * @param {User | null | undefined} current_user The signed-in admin.
 * @param {User} target_user The user row being acted on.
 * @returns {boolean}
 */
export const canManageUser = (
  current_user: User | null | undefined,
  target_user: User
): boolean => {
  if (!current_user) return false;
  if (current_user.email === target_user.email) return true;
  if (current_user.super_admin) return true;
  if (target_user.super_admin) return false;
  return getAdminGroups(target_user).length === 0;
};

/**
 * Get all group IDs that a non super admin user has any access to.
 * @param {User} user User to check group access for.
 * @returns {Array<string>}
 */
export const getUserGroups = (user: User): Array<string> => {
  return Object.keys(user.groups);
};

/**
 * Get all group IDs that a non super admin has admin access to.
 * @param {User} user User to check group admin access for.
 * @returns {Array<string>}
 */
export const getAdminGroups = (user: User): Array<string> => {
  return Object.keys(user.groups).filter((group_id: string) => {
    return user.groups[group_id].admin;
  });
};

/**
 * Check if user is a group admin for a particular group ID.
 * @param {User} user User to check access for.
 * @param {string} group_id Group to check admin access for.
 * @returns {boolean}
 */
export const checkUserGroupAdmin = (user: User, group_id: string): boolean => {
  const admin_group_ids: Array<string> = getAdminGroups(user);
  return user.super_admin || admin_group_ids.includes(group_id);
};

/**
 * Scopes a list of user rows to what the viewer is allowed to see: every row
 * for a super admin, otherwise only non-super-admin rows that share at least
 * one group the viewer administers. A group admin with no admin groups sees
 * nothing.
 *
 * Super-admin rows are excluded explicitly rather than relying on them having
 * no group data: toggling a user to super admin (Edit → Access) does not
 * clear their existing `groups`, so a promoted user can retain membership in
 * a group the viewer administers — without this check they would leak into
 * the group admin's view.
 *
 * Shared by every surface that lists other users for a group admin (the user
 * table, the "Assign to company" dialog's user picker, etc.) so scoping can't
 * drift between them.
 * @param {User | null | undefined} viewer The signed-in user.
 * @param {Array<User>} rows Candidate user rows to scope.
 * @returns {Array<User>}
 */
export const getVisibleUserRows = (
  viewer: User | null | undefined,
  rows: Array<User>
): Array<User> => {
  if (!viewer) return [];
  if (viewer.super_admin) return rows;
  const adminGroupSet = new Set(getAdminGroups(viewer));
  if (adminGroupSet.size === 0) return [];
  return rows.filter(
    (row) => !row.super_admin && Object.keys(row.groups ?? {}).some((gid) => adminGroupSet.has(gid))
  );
};

/**
 * Get all of the existing configured company IDs on all of the configured groups for a user.
 * @param {User} user User object to parse for companies.
 * @returns {Array<string>}
 */
export const getUserCompanies = (user: User): Array<string> => {
  const user_groups: Array<string> = getUserGroups(user);
  const user_companies: Array<string> = user_groups.reduce(
    (companies: Array<string>, current_group: string) => {
      return [...Object.keys(user.groups[current_group]?.companies ?? {}), ...companies];
    },
    []
  );
  return user_companies;
};

/**
 * Check whether a user has access to a company.
 * @param {User} user User to check access for.
 * @param {CompanyExtended} company Company to check user access for.
 * @returns {boolean}
 */
export const userAllowedCompany = (user: User, company: CompanyExtended): boolean => {
  if (user.super_admin) return true;
  const admin_group_ids: Array<string> = getAdminGroups(user);
  if (admin_group_ids.includes(company.group.id)) return true;
  if (user.groups[company.group.id]?.companies?.[company.id]) return true;
  return false;
};

/**
 * Get the given company role for a user.
 * @param {User} user User to get company role for.
 * @param {string} group_id Group to get company role for.
 * @param {string} company_id Company to get role for.
 * @returns {string}
 */
export const getCompanyRole = (
  user: User,
  group_id: string,
  company_id: string
): string | undefined => {
  return user.groups[group_id]?.companies?.[company_id]?.role;
};

/**
 * Filter a list of potential views to get user accessible ones based on user permissions, configured powerbi reports and configured navigation links.
 * @param {User} user The user to check views against.
 * @param {string} group_id The group to check view permissions for.
 * @param {string} company_id The company to check permissions for.
 * @param {Array<ViewExtended> | undefined} views_list Views to check permissions for, if not provided, check all application views.
 * @param {Array<PBIReport> | undefined} pbi_reports Optionally check views for configured powerbi report company access.
 * @returns {Array<View>}
 */
export const getUserViews = (
  user: User,
  group_id: string,
  company_id: string,
  views_list?: Array<ViewExtended>,
  pbi_reports?: Array<PBIReport>
): Array<View> => {
  const views: Array<ViewExtended> = views_list ? views_list : getViews();
  const allowed_views: Array<View> = views.filter((view: ViewExtended) => {
    // Check for configured pbi report for view if required.
    const valid_pbi: boolean =
      pbi_reports && view.require_pbi_report
        ? !!pbi_reports.find((pbi_report: PBIReport) => pbi_report.view === view.id)
        : true;
    return (
      valid_pbi &&
      getViewAccess(user, group_id, company_id, `${view.module.id}.${view.id}`) !== 'none'
    );
  });
  return allowed_views;
};

// Map the current permission key to any old versions of the same permission for backwards compatibility.
const PERMISSION_ALIASES: Record<string, Array<string>> = {
  'resource_centre.explore_resources': ['resource_centre.dashboard'],
  'exports.export_data': ['sustainability.exports'],
  'asset_management.syntiroLive': ['asset_management.live_monitoring']
};

// Expand a single canonical permission into all valid variants for backwards compatibliity.
const resolvePermissions = (permission: string): Array<string> => {
  return [permission, ...(PERMISSION_ALIASES[permission] || [])];
};

/**
 * Find the access level granted for a `{module_id}.{view_id}` key, or undefined.
 *
 * The single place that decides what a stored view permission means. It exists because the
 * same decision was previously re-derived at every call site with a bare `startsWith`, and
 * those implementations disagreed: a view whose id is a prefix of another view's id was
 * matched by the longer view's permission. `asset_management.refrigeration_plants` and
 * `asset_management.refrigeration_plants_caas` are a real such pair.
 *
 * Matching is anchored on the `.` before the access level, and `write` wins over `read` when
 * a view somehow carries both — array order is not meaningful.
 *
 * Aliases are NOT expanded here; callers that need backwards compatibility expand first
 * (see {@link getViewAccess}). The permission grid deliberately does not, because it must
 * show and edit the canonical key rather than silently rewrite a legacy one.
 */
export const findViewPermissionAccess = (
  company_permissions: Array<string>,
  view_permission: string
): 'read' | 'write' | undefined => {
  let has_read_access = false;
  for (const company_permission of company_permissions) {
    if (company_permission === `${view_permission}.write`) return 'write';
    if (company_permission === `${view_permission}.read`) has_read_access = true;
  }
  return has_read_access ? 'read' : undefined;
};

/**
 * Remove every entry granting a `{module_id}.{view_id}` key, leaving siblings intact.
 *
 * The counterpart to {@link findViewPermissionAccess} on the write side. An unanchored
 * `!startsWith(`${module}.${view}`)` filter also strips the *other* view whose id extends
 * this one — so editing Refrigeration Plants silently deleted the Refrigeration Plants
 * (CaaS) grant.
 */
export const withoutViewPermission = (
  company_permissions: Array<string>,
  view_permission: string
): Array<string> =>
  company_permissions.filter(
    (company_permission: string) =>
      company_permission !== `${view_permission}.read` &&
      company_permission !== `${view_permission}.write`
  );

/**
 * Count the permissions belonging to a module, anchored so a module id that is a prefix of
 * another cannot inflate the count. No such pair exists today; this keeps it that way.
 */
export const countModulePermissions = (
  company_permissions: Array<string>,
  module_id: string
): number =>
  company_permissions.filter((company_permission: string) =>
    company_permission.startsWith(`${module_id}.`)
  ).length;

/**
 * Get the access level a given user has for a view for a company, with short-term backwards compatibility for old permission mappings.
 * @param {User} user The user to check access for.
 * @param {string} group_id The Group to check view access for.
 * @param {string} company_id The company to  check view access for.
 * @param {string} view_permission The module.view permission to look for in the user permissions object, ie sustainability.readiness_gauge.
 * @returns {ViewAccess}
 */
export const getViewAccess = (
  user: User,
  group_id: string,
  company_id: string,
  view_permission: string
): ViewAccess => {
  if (user.super_admin) {
    return 'admin';
  }
  if (user.groups[group_id]?.admin) {
    return 'admin';
  }

  const user_group: UserGroupRole = user.groups[group_id];
  const user_company: UserCompanyRole | undefined = user_group?.companies?.[company_id];
  const role: string | undefined = user_company?.role;

  if (role === 'editor' && view_permission !== 'configuration') {
    return 'write';
  }

  if (role === 'viewer' && view_permission !== 'configuration') {
    return 'read';
  }

  const company_permissions: Array<string> = user_company?.permissions ?? [];

  // Canonical key first, then any legacy aliases of it. `resolvePermissions` is the single
  // source of that expansion — spreading PERMISSION_ALIASES here as well would duplicate
  // every alias and leave two implementations to diverge.
  const expanded_permissions: Array<string> = resolvePermissions(view_permission);

  // Check permissions via the shared matcher, so enforcement and the permission grid cannot
  // disagree about what a stored permission means. Anchoring also means a three-segment
  // string can no longer satisfy a bare module-id lookup, so `configuration.<anything>.write`
  // cannot answer `getViewAccess(..., 'configuration')`.
  // See PRD_CUSTOM_ROLES.md §3.1 and §3.5 (DA-8).
  //
  // The first key that grants anything wins, so the canonical key still takes precedence over
  // a legacy alias of it. `write` beats `read` only *within* one key — deciding that across
  // keys would let a stale `sustainability.exports.write`, which the grid neither shows nor
  // clears, silently override an explicitly-set `exports.export_data.read`.
  for (const permission of expanded_permissions) {
    const access: 'read' | 'write' | undefined = findViewPermissionAccess(
      company_permissions,
      permission
    );
    if (access) return access;
  }

  return 'none';
};

/**
 * Get set of permission IDs relating to given entities for the user's currently selected company.
 * @param {Array<string>} company_permissions List of full company permissions for user.
 * @param {string} permissions_key Entity key to filter permissions for.
 * @returns {Set<string>}
 */
export const getCurrentEntityPermissionIDs = (
  company_permissions: Array<string>,
  permissions_key: string
): Set<string> => {
  return new Set<string>(
    company_permissions
      .filter((permission: string) => permission.startsWith(`${permissions_key}.`))
      .map((permission: string) => permission.split('.')[1])
  );
};

/**
 * Get the selected entity list for given key based on current permissions.
 * @param {Array<string>} company_permissions List of full company permissions for user.
 * @param {Array<T>} entities_list Full list of entities containing IDs to filter.
 * @param {string} permissions_key Entity key to filter permissions for.
 * @returns {Array<T>}
 */
export const getPermissionEntities = <T extends { id: string }>(
  company_permissions: Array<string>,
  options_list: Array<T>,
  permissions_key: string
): Array<T> => {
  const current_entity_permissions: Set<string> = getCurrentEntityPermissionIDs(
    company_permissions,
    permissions_key
  );
  return options_list.filter((option: T) => current_entity_permissions.has(option.id));
};

/**
 * Get the configured company metrics that a user has access to.
 * @param {User} user The user to check metric access for.
 * @param {string} group_id The Group to check metric access for.
 * @param {string} company_id The company to check metric access for.
 * @returns {Promise<Array<MetricExtended>>}
 */
export const getUserMetrics = async (
  user: User,
  group_id: string,
  company_id: string
): Promise<Array<MetricExtended>> => {
  const metrics: Array<MetricExtended> = await getMetrics(
    false,
    group_id,
    company_id,
    undefined,
    true
  );
  // If user is a super or group admin or viewer / editor, return all metrics for company
  if (
    user.super_admin ||
    user.groups?.[group_id]?.admin ||
    user.groups?.[group_id]?.companies?.[company_id]?.role === 'viewer' ||
    user.groups?.[group_id]?.companies?.[company_id]?.role === 'editor'
  ) {
    return metrics;
  }

  const user_metrics: Array<MetricExtended> = [];
  const user_metric_ids: Array<string> = (
    user.groups?.[group_id]?.companies?.[company_id]?.permissions || []
  )
    .filter((permission: string) => {
      return permission.startsWith('metric.');
    })
    .map((perm: string) => perm.replace('metric.', ''));

  metrics.forEach((metric: MetricExtended) => {
    if (user_metric_ids.includes(metric.id)) user_metrics.push(metric);
  });

  return user_metrics;
};

/**
 * Get the configured company countries that a user has access to based on country access.
 * @param {User} user The user to check country access for.
 * @param {string} group_id The Group to check country access for.
 * @param {string} company_id The company to check country access for.
 * @returns {Promise<Array<Country>>}
 */
export const getUserCountries = async (
  user: User,
  group_id: string,
  company_id: string
): Promise<Array<Country>> => {
  const countries: Array<Country> = await getCountries(false, group_id, company_id);
  const user_company: UserCompanyRoles['company_id'] | undefined =
    user.groups?.[group_id]?.companies?.[company_id];
  const user_company_role: string = user_company?.role ?? '';
  // If user is a super or group admin or viewer / editor, return all countries for company
  if (
    user.super_admin ||
    user.groups?.[group_id]?.admin ||
    user_company_role === 'viewer' ||
    user_company_role === 'editor'
  ) {
    return countries;
  }

  const user_country_ids = new Set<string>(
    (user_company?.permissions || [])
      .filter((permission: string) => permission.startsWith('country'))
      .map((permission: string) => permission.replace('country.', ''))
  );

  return countries.filter((country: Country) => user_country_ids.has(country.id));
};

/**
 * Get the configured company regions that a user has access to based on country and region access.
 * @param {User} user The user to check region access for.
 * @param {string} group_id The Group to check region access for.
 * @param {string} company_id The company to check region access for.
 * @returns {Promise<Array<Region>>}
 */
export const getUserRegions = async (
  user: User,
  group_id: string,
  company_id: string
): Promise<Array<Region>> => {
  const regions: Array<Region> = await getRegions(false, group_id, company_id);
  const user_company: UserCompanyRoles['company_id'] | undefined =
    user.groups?.[group_id]?.companies?.[company_id];
  const user_company_role: string = user_company?.role ?? '';
  // If user is a super or group admin or viewer / editor, return all regions for company
  if (
    user.super_admin ||
    user.groups?.[group_id]?.admin ||
    user_company_role === 'viewer' ||
    user_company_role === 'editor'
  ) {
    return regions;
  }
  const user_countries: Array<Country> = await getUserCountries(user, group_id, company_id);
  const user_country_ids = new Set<string>(user_countries.map(({ id }) => id));
  const user_region_ids = new Set<string>(
    (user_company?.permissions || [])
      .filter((permission: string) => permission.startsWith('region'))
      .map((permission: string) => permission.replace('region.', ''))
  );

  return regions.filter(
    (region: Region) => user_region_ids.has(region.id) || user_country_ids.has(region.country.id)
  );
};

/**
 * Get the configured company divisions that a user has access to based on division access.
 * @param {User} user The user to check division access for.
 * @param {string} group_id The Group to check division access for.
 * @param {string} company_id The company to check division access for.
 * @returns {Promise<Array<Division>>}
 */
export const getUserDivisions = async (
  user: User,
  group_id: string,
  company_id: string
): Promise<Array<Division>> => {
  const divisions: Array<Division> = await getDivisions(group_id, company_id);
  const user_company: UserCompanyRoles['company_id'] | undefined =
    user.groups?.[group_id]?.companies?.[company_id];
  const user_company_role: string = user_company?.role ?? '';
  // If user is a super or group admin or viewer / editor, return all divisions for company
  if (
    user.super_admin ||
    user.groups?.[group_id]?.admin ||
    user_company_role === 'viewer' ||
    user_company_role === 'editor'
  ) {
    return divisions;
  }
  const user_division_ids = new Set<string>(
    (user_company?.permissions || [])
      .filter((permission: string) => permission.startsWith('division'))
      .map((permission: string) => permission.replace('division.', ''))
  );

  return divisions.filter((division: Division) => user_division_ids.has(division.id));
};

/**
 * Get the configured company subdivisions that a user has access to based on division and subdivision access.
 * @param {User} user The user to check subdivision access for.
 * @param {string} group_id The Group to check subdivision access for.
 * @param {string} company_id The company to check subdivision access for.
 * @returns {Promise<Array<Subdivision>>}
 */
export const getUserSubdivisions = async (
  user: User,
  group_id: string,
  company_id: string
): Promise<Array<Subdivision>> => {
  const subdivisions: Array<Subdivision> = await getSubdivisions(group_id, company_id);
  const user_company: UserCompanyRoles['company_id'] | undefined =
    user.groups?.[group_id]?.companies?.[company_id];
  const user_company_role: string = user_company?.role ?? '';
  // If user is a super or group admin or viewer / editor, return all subdivisions for company
  if (
    user.super_admin ||
    user.groups?.[group_id]?.admin ||
    user_company_role === 'viewer' ||
    user_company_role === 'editor'
  ) {
    return subdivisions;
  }
  const user_divisions: Array<Division> = await getUserDivisions(user, group_id, company_id);
  const user_division_ids = new Set<string>(user_divisions.map(({ id }) => id));
  const user_subdivision_ids = new Set<string>(
    (user_company?.permissions || [])
      .filter((permission: string) => permission.startsWith('subdivision'))
      .map((permission: string) => permission.replace('subdivision.', ''))
  );

  return subdivisions.filter(
    (subdivision: Subdivision) =>
      user_subdivision_ids.has(subdivision.id) || user_division_ids.has(subdivision.division.id)
  );
};

/**
 * Get the configured company sites that a user has access to based on country, region, division, subdivision and site access.
 * @param {User} user The user to check site access for.
 * @param {string} group_id The Group to check site access for.
 * @param {string} company_id The company to check site access for.
 * @returns {Promise<Array<Site>>}
 */
export const getUserSites = async (
  user: User,
  group_id: string,
  company_id: string
): Promise<Array<SiteExtended>> => {
  const sites: Array<SiteExtended> = await getSitesJoinParents(group_id, company_id);
  const user_company: UserCompanyRoles['company_id'] | undefined =
    user.groups?.[group_id]?.companies?.[company_id];
  const user_company_role: string = user_company?.role ?? '';
  // If user is a super or group admin or viewer / editor, return all sites for company
  if (
    user.super_admin ||
    user.groups?.[group_id]?.admin ||
    user_company_role === 'viewer' ||
    user_company_role === 'editor'
  ) {
    return sites;
  }

  const [user_countries, user_regions, user_divisions, user_subdivisions]: [
    Array<Country>,
    Array<Region>,
    Array<Division>,
    Array<Subdivision>
  ] = await Promise.all([
    getUserCountries(user, group_id, company_id),
    getUserRegions(user, group_id, company_id),
    getUserDivisions(user, group_id, company_id),
    getUserSubdivisions(user, group_id, company_id)
  ]);
  const user_country_ids = new Set<string>(user_countries.map(({ id }) => id));
  const user_region_ids = new Set<string>(user_regions.map(({ id }) => id));
  const user_division_ids = new Set<string>(user_divisions.map(({ id }) => id));
  const user_subdivision_ids = new Set<string>(user_subdivisions.map(({ id }) => id));
  const user_site_ids = new Set<string>(
    (user_company?.permissions || [])
      .filter((permission: string) => permission.startsWith('site'))
      .map((permission: string) => permission.replace('site.', ''))
  );

  return sites.filter(
    (site: SiteExtended) =>
      user_site_ids.has(site.id) ||
      user_subdivision_ids.has(site.subdivision.id) ||
      user_division_ids.has(site.subdivision.division.id) ||
      user_region_ids.has(site.region.id) ||
      user_country_ids.has(site.region.country.id)
  );
};

/**
 * Get the configured company external companies that a user has access to.
 * @param {User} user The user to check external company access for.
 * @param {string} group_id The Group to check external company access for.
 * @param {string} company_id The company to check external company access for.
 * @returns {Promise<Array<ExternalCompany>>}
 */
export const getUserExternalCompanyList = async (
  user: User,
  group_id: string,
  company_id: string
): Promise<Array<ExternalCompany>> => {
  const external_companies: Array<ExternalCompany> = await getExternalCompanies(
    group_id,
    company_id
  );
  // If user is a super or group admin or viewer / editor, return all external companies for company
  if (
    user.super_admin ||
    user.groups?.[group_id]?.admin ||
    user.groups?.[group_id]?.companies?.[company_id]?.role === 'viewer' ||
    user.groups?.[group_id]?.companies?.[company_id]?.role === 'editor'
  ) {
    return external_companies;
  }

  const user_external_companies: Array<ExternalCompany> = [];
  const user_external_company_ids: Array<string> = (
    user.groups?.[group_id]?.companies?.[company_id]?.permissions || []
  )
    .filter((permission: string) => {
      return permission.startsWith('external_company');
    })
    .map((perm: string) => perm.replace('external_company.', ''));

  external_companies.forEach((external_company: ExternalCompany) => {
    if (user_external_company_ids.includes(external_company.id))
      user_external_companies.push(external_company);
  });

  return user_external_companies;
};

/**
 * Get the metric capturing actvity owner options for a user based on their type and given external companies.
 * @param {User} user User to get options for.
 * @param {string} group_id ID of Group to check company permissions for.
 * @param {CompanyExtended} company Company with group information to use for permission checking and internal company option.
 * @param {Array<ExternalCompany>} external_companies External companies to parse and include in options.
 */
export const getUserActivityOwners = (
  user: User,
  group_id: string,
  company: Company,
  external_companies: Array<ExternalCompany>
): Array<ActivityOwner> => {
  // Determine combination (internal and external) company access.
  const combination_company_access: boolean =
    user.super_admin || checkUserGroupAdmin(user, group_id) || user.type === 'any';
  const internal_activity_owner: ActivityOwner = {
    id: company.id,
    name: company.name,
    scope: null,
    is_external: false
  };
  const external_activity_owners: Array<ActivityOwner> = external_companies.map(
    (external_company: ExternalCompany) => {
      return {
        ...external_company,
        is_external: true
      };
    }
  );
  // Return full access for combination users, just external companies for external users, or just internal company for internal users.
  return combination_company_access
    ? [internal_activity_owner, ...external_activity_owners]
    : user.type === 'external'
      ? [...external_activity_owners]
      : [internal_activity_owner];
};

/**
 * Get full list of user types for the platform.
 * @returns {Array<UserType>}
 */
export const getUserTypes = (): Array<UserType> => {
  return [
    {
      id: 'internal',
      name: 'Internal'
    },
    {
      id: 'external',
      name: 'External'
    },
    {
      id: 'any',
      name: 'Combination'
    }
  ];
};

/**
 * Check whether a view should be shown for the current user and company.
 * Returns false if the view is the custom dashboard and the company has no custom dashboard configured,
 * or if the user has no access to the view.
 * @param {string} viewId The view ID to check availability for.
 * @param {User | null} user The current user.
 * @param {string} groupId The group ID to check access for.
 * @param {string | null | undefined} companyId The company ID to check access for.
 * @returns {boolean}
 */
export function isViewAvailable(
  viewId: string,
  user: User | null,
  groupId: string,
  companyId: string | null | undefined
): boolean {
  if (viewId === CUSTOM_DASHBOARD_VIEW_ID && !hasCustomDashboard(companyId)) {
    return false;
  }
  if (!user || !companyId) return false;
  const moduleId = getModuleId(viewId);
  const access = getViewAccess(user, groupId, companyId, `${moduleId}.${viewId}`);
  return access !== 'none';
}
